import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { BiSolidInbox } from 'react-icons/bi';
import {
  createLookupValue,
  deactivateLookupValue,
  listLookupResource,
  updateLookupValue,
  type LookupResourceKey,
} from '@/api/lookups.api';
import { ApiError, type ApiErrorDetail } from '@/api/errors';
import { Banner, type BannerTone } from '@/components/common/Banner';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { StatusChip } from '@/components/common/StatusChip';
import { FormField } from '@/components/form/FormField';
import { DataTable, type DataTableColumn } from '@/components/table/DataTable';
import { EmptyState } from '@/components/table/EmptyState';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import { lookupLabel } from '@/lib/lookups';
import type { LookupItem } from '@/types/shared';
import './LookupManager.css';

interface LookupManagerProps {
  resource: LookupResourceKey;
  /** i18n key for this resource's display name (e.g. `lookupsSettings.tabs.grades`), used to build
   * messages like "Add a new {{list}} value". */
  resourceLabelKey: string;
}

interface LabelFieldErrors {
  labelAr?: string;
  labelEn?: string;
}

function fieldErrorsFromDetails(details: ApiErrorDetail[]): LabelFieldErrors {
  const errors: LabelFieldErrors = {};
  for (const detail of details) {
    if (detail.field === 'labelAr') errors.labelAr = detail.message;
    if (detail.field === 'labelEn') errors.labelEn = detail.message;
  }
  return errors;
}

/**
 * CRUD manager for a single reference list (grades / agreed schools / educational
 * administrations) — used once per tab in `LookupsPage`. Wires directly to the existing
 * `/lookups/{resource}` endpoints (`lookups.api.ts`): `GET` (active + inactive) for the table,
 * `POST` to add, `PATCH` to rename and/or reactivate, `DELETE` to deactivate.
 */
export function LookupManager({ resource, resourceLabelKey }: LookupManagerProps) {
  const { t, i18n } = useTranslation();
  const resourceLabel = t(resourceLabelKey);

  const [items, setItems] = useState<LookupItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [banner, setBanner] = useState<{ tone: BannerTone; text: string } | null>(null);
  const [searchInput, setSearchInput] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [addLabelAr, setAddLabelAr] = useState('');
  const [addLabelEn, setAddLabelEn] = useState('');
  const [addErrors, setAddErrors] = useState<LabelFieldErrors>({});
  const [addSubmitting, setAddSubmitting] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editLabelAr, setEditLabelAr] = useState('');
  const [editLabelEn, setEditLabelEn] = useState('');
  const [editErrors, setEditErrors] = useState<LabelFieldErrors>({});
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [confirmDeactivateItem, setConfirmDeactivateItem] = useState<LookupItem | null>(null);
  const [deactivateSubmitting, setDeactivateSubmitting] = useState(false);
  const [activatingId, setActivatingId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    listLookupResource(resource, { activeOnly: false })
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch(() => {
        if (!cancelled) setLoadError(t('lookupsSettings.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [resource, reloadToken, t]);

  function refetch() {
    setReloadToken((v) => v + 1);
  }

  function genericErrorMessage(apiError: ApiError): string {
    return apiError.code === 'NETWORK_ERROR'
      ? t('lookupsSettings.form.networkError')
      : apiError.message || t('lookupsSettings.form.genericError');
  }

  const filtered = (items ?? []).filter((item) => {
    const needle = searchInput.trim().toLocaleLowerCase();
    if (!needle) return true;
    return (
      item.labelAr.toLocaleLowerCase().includes(needle) ||
      item.labelEn.toLocaleLowerCase().includes(needle)
    );
  });

  function validateLabels(labelAr: string, labelEn: string): LabelFieldErrors {
    const errors: LabelFieldErrors = {};
    if (!labelAr.trim()) errors.labelAr = t('lookupsSettings.form.labelArRequired');
    if (!labelEn.trim()) errors.labelEn = t('lookupsSettings.form.labelEnRequired');
    return errors;
  }

  function resetAddForm() {
    setAddLabelAr('');
    setAddLabelEn('');
    setAddErrors({});
  }

  function openAddForm() {
    resetAddForm();
    setAddOpen(true);
  }

  function closeAddForm() {
    setAddOpen(false);
    resetAddForm();
  }

  async function handleAddSubmit(e: FormEvent) {
    e.preventDefault();
    setBanner(null);

    const errors = validateLabels(addLabelAr, addLabelEn);
    setAddErrors(errors);
    if (Object.keys(errors).length > 0) {
      document.getElementById(errors.labelAr ? 'add-labelAr' : 'add-labelEn')?.focus();
      return;
    }

    setAddSubmitting(true);
    try {
      const created = await createLookupValue(resource, {
        labelAr: addLabelAr.trim(),
        labelEn: addLabelEn.trim(),
      });
      const message = t('lookupsSettings.form.createSuccess', {
        label: lookupLabel(created, i18n.language),
      });
      setBanner({
        tone: 'success',
        text: message,
      });
      showSuccessToast(message);
      closeAddForm();
      refetch();
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      const fieldErrors = fieldErrorsFromDetails(apiError.details);
      if (Object.keys(fieldErrors).length > 0) {
        setAddErrors(fieldErrors);
      } else {
        const message = genericErrorMessage(apiError);
        setBanner({ tone: 'danger', text: message });
        if (apiError.code === 'NETWORK_ERROR') showErrorToast(message);
      }
    } finally {
      setAddSubmitting(false);
    }
  }

  function startEdit(item: LookupItem) {
    setBanner(null);
    setEditingId(item.id);
    setEditLabelAr(item.labelAr);
    setEditLabelEn(item.labelEn);
    setEditErrors({});
  }

  function cancelEdit() {
    setEditingId(null);
    setEditErrors({});
  }

  async function handleEditSave(item: LookupItem) {
    setBanner(null);
    const errors = validateLabels(editLabelAr, editLabelEn);
    setEditErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setEditSubmitting(true);
    try {
      const updated = await updateLookupValue(resource, item.id, {
        labelAr: editLabelAr.trim(),
        labelEn: editLabelEn.trim(),
      });
      const message = t('lookupsSettings.form.updateSuccess', {
        label: lookupLabel(updated, i18n.language),
      });
      setBanner({
        tone: 'success',
        text: message,
      });
      showSuccessToast(message);
      setEditingId(null);
      refetch();
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      const fieldErrors = fieldErrorsFromDetails(apiError.details);
      if (Object.keys(fieldErrors).length > 0) {
        setEditErrors(fieldErrors);
      } else {
        const message = genericErrorMessage(apiError);
        setBanner({ tone: 'danger', text: message });
        if (apiError.code === 'NETWORK_ERROR') showErrorToast(message);
      }
    } finally {
      setEditSubmitting(false);
    }
  }

  async function handleActivate(item: LookupItem) {
    setBanner(null);
    setActivatingId(item.id);
    try {
      const updated = await updateLookupValue(resource, item.id, {
        labelAr: item.labelAr,
        labelEn: item.labelEn,
        isActive: true,
      });
      const message = t('lookupsSettings.form.activateSuccess', {
        label: lookupLabel(updated, i18n.language),
      });
      setBanner({
        tone: 'success',
        text: message,
      });
      showSuccessToast(message);
      refetch();
    } catch (err) {
      setBanner({ tone: 'danger', text: genericErrorMessage(ApiError.fromUnknown(err)) });
    } finally {
      setActivatingId(null);
    }
  }

  async function handleConfirmDeactivate() {
    if (!confirmDeactivateItem) return;
    setBanner(null);
    setDeactivateSubmitting(true);
    try {
      await deactivateLookupValue(resource, confirmDeactivateItem.id);
      const message = t('lookupsSettings.form.deactivateSuccess', {
        label: lookupLabel(confirmDeactivateItem, i18n.language),
      });
      setBanner({
        tone: 'success',
        text: message,
      });
      showSuccessToast(message);
      setConfirmDeactivateItem(null);
      refetch();
    } catch (err) {
      setBanner({ tone: 'danger', text: genericErrorMessage(ApiError.fromUnknown(err)) });
      setConfirmDeactivateItem(null);
    } finally {
      setDeactivateSubmitting(false);
    }
  }

  const columns: DataTableColumn<LookupItem>[] = [
    {
      key: 'labelAr',
      header: t('lookupsSettings.columns.labelAr'),
      render: (row) => {
        if (editingId !== row.id) return <bdi>{row.labelAr}</bdi>;
        const errorId = editErrors.labelAr ? `edit-labelAr-${row.id}-error` : undefined;
        return (
          <div className="fa-lookup-manager__row-field">
            <input
              type="text"
              dir="rtl"
              id={`edit-labelAr-${row.id}`}
              aria-label={t('lookupsSettings.form.labelAr')}
              aria-invalid={Boolean(editErrors.labelAr)}
              aria-describedby={errorId}
              value={editLabelAr}
              disabled={editSubmitting}
              onChange={(e) => {
                setEditLabelAr(e.target.value);
                setEditErrors((prev) => ({ ...prev, labelAr: undefined }));
              }}
              className="fa-lookup-manager__row-input"
            />
            {editErrors.labelAr && (
              <p id={errorId} className="fa-lookup-manager__row-error">
                {editErrors.labelAr}
              </p>
            )}
          </div>
        );
      },
    },
    {
      key: 'labelEn',
      header: t('lookupsSettings.columns.labelEn'),
      render: (row) => {
        if (editingId !== row.id) return <bdi>{row.labelEn}</bdi>;
        const errorId = editErrors.labelEn ? `edit-labelEn-${row.id}-error` : undefined;
        return (
          <div className="fa-lookup-manager__row-field">
            <input
              type="text"
              dir="ltr"
              id={`edit-labelEn-${row.id}`}
              aria-label={t('lookupsSettings.form.labelEn')}
              aria-invalid={Boolean(editErrors.labelEn)}
              aria-describedby={errorId}
              value={editLabelEn}
              disabled={editSubmitting}
              onChange={(e) => {
                setEditLabelEn(e.target.value);
                setEditErrors((prev) => ({ ...prev, labelEn: undefined }));
              }}
              className="fa-lookup-manager__row-input fa-ltr-isolate"
            />
            {editErrors.labelEn && (
              <p id={errorId} className="fa-lookup-manager__row-error">
                {editErrors.labelEn}
              </p>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: t('lookupsSettings.columns.status'),
      render: (row) => (
        <StatusChip
          label={
            row.isActive ? t('lookupsSettings.status.active') : t('lookupsSettings.status.inactive')
          }
          tone={row.isActive ? 'success' : 'neutral'}
        />
      ),
    },
    {
      key: 'actions',
      header: t('lookupsSettings.columns.actions'),
      cellClassName: 'fa-lookup-manager__actions-cell',
      render: (row) => {
        const label = lookupLabel(row, i18n.language);

        if (editingId === row.id) {
          return (
            <div className="fa-lookup-manager__row-actions">
              <button
                type="button"
                className="fa-btn fa-btn--primary"
                disabled={editSubmitting}
                onClick={() => handleEditSave(row)}
              >
                {editSubmitting
                  ? t('lookupsSettings.savingButton')
                  : t('lookupsSettings.saveButton')}
              </button>
              <button
                type="button"
                className="fa-btn fa-btn--secondary"
                disabled={editSubmitting}
                onClick={cancelEdit}
              >
                {t('lookupsSettings.cancelButton')}
              </button>
            </div>
          );
        }

        return (
          <div className="fa-lookup-manager__row-actions">
            <button
              type="button"
              className="fa-btn fa-btn--ghost"
              aria-label={t('lookupsSettings.editAriaLabel', { label })}
              onClick={() => startEdit(row)}
            >
              {t('lookupsSettings.editButton')}
            </button>
            {row.isActive ? (
              <button
                type="button"
                className="fa-btn fa-btn--secondary"
                aria-label={t('lookupsSettings.deactivateAriaLabel', { label })}
                onClick={() => setConfirmDeactivateItem(row)}
              >
                {t('lookupsSettings.deactivateButton')}
              </button>
            ) : (
              <button
                type="button"
                className="fa-btn fa-btn--secondary"
                aria-label={t('lookupsSettings.activateAriaLabel', { label })}
                disabled={activatingId === row.id}
                onClick={() => handleActivate(row)}
              >
                {activatingId === row.id
                  ? t('lookupsSettings.savingButton')
                  : t('lookupsSettings.activateButton')}
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const hasSearch = Boolean(searchInput.trim());
  const emptyState = hasSearch ? (
    <EmptyState
      icon={BiSolidInbox}
      title={t('lookupsSettings.empty.noMatchesTitle')}
      hint={t('lookupsSettings.empty.noMatchesHint')}
      action={
        <button
          type="button"
          className="fa-btn fa-btn--secondary"
          onClick={() => setSearchInput('')}
        >
          {t('lookupsSettings.empty.clearSearch')}
        </button>
      }
    />
  ) : (
    <EmptyState
      icon={BiSolidInbox}
      title={t('lookupsSettings.empty.noRecordsTitle')}
      hint={t('lookupsSettings.empty.noRecordsHint')}
    />
  );

  return (
    <div className="fa-lookup-manager">
      {banner && (
        <Banner tone={banner.tone} assertive={banner.tone === 'danger'}>
          {banner.text}
        </Banner>
      )}

      {!addOpen && (
        <div className="fa-lookup-manager__toolbar-top">
          <button type="button" className="fa-btn fa-btn--primary" onClick={openAddForm}>
            {t('lookupsSettings.addButton')}
          </button>
        </div>
      )}

      {addOpen && (
        <form
          className="fa-lookup-manager__add-form fa-form-section"
          onSubmit={handleAddSubmit}
          noValidate
        >
          <h2 className="fa-form-section__title">
            {t('lookupsSettings.addPanelTitle', { list: resourceLabel })}
          </h2>
          <div className="fa-field-row">
            <FormField
              id="add-labelAr"
              label={t('lookupsSettings.form.labelAr')}
              required
              error={addErrors.labelAr}
            >
              <input
                type="text"
                dir="rtl"
                value={addLabelAr}
                disabled={addSubmitting}
                onChange={(e) => {
                  setAddLabelAr(e.target.value);
                  setAddErrors((prev) => ({ ...prev, labelAr: undefined }));
                }}
              />
            </FormField>
            <FormField
              id="add-labelEn"
              label={t('lookupsSettings.form.labelEn')}
              required
              error={addErrors.labelEn}
            >
              <input
                type="text"
                dir="ltr"
                className="fa-ltr-isolate"
                value={addLabelEn}
                disabled={addSubmitting}
                onChange={(e) => {
                  setAddLabelEn(e.target.value);
                  setAddErrors((prev) => ({ ...prev, labelEn: undefined }));
                }}
              />
            </FormField>
          </div>
          <div className="fa-form-actions">
            <button
              type="button"
              className="fa-btn fa-btn--secondary"
              disabled={addSubmitting}
              onClick={closeAddForm}
            >
              {t('lookupsSettings.cancelButton')}
            </button>
            <button type="submit" className="fa-btn fa-btn--primary" disabled={addSubmitting}>
              {addSubmitting ? t('lookupsSettings.savingButton') : t('lookupsSettings.saveButton')}
            </button>
          </div>
        </form>
      )}

      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={(row) => String(row.id)}
        caption={t('lookupsSettings.tableCaption', { list: resourceLabel })}
        meta={null}
        loading={loading}
        error={loadError}
        onRetry={refetch}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          placeholder: t('lookupsSettings.searchPlaceholder'),
          ariaLabel: t('lookupsSettings.searchAriaLabel'),
        }}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        showExportButton={false}
        emptyState={emptyState}
      />

      <ConfirmDialog
        open={Boolean(confirmDeactivateItem)}
        title={t('lookupsSettings.deactivateConfirm.title')}
        description={
          confirmDeactivateItem
            ? t('lookupsSettings.deactivateConfirm.body', {
                label: lookupLabel(confirmDeactivateItem, i18n.language),
              })
            : undefined
        }
        confirmLabel={t('lookupsSettings.deactivateConfirm.confirm')}
        cancelLabel={t('lookupsSettings.deactivateConfirm.cancel')}
        destructive
        confirmDisabled={deactivateSubmitting}
        onConfirm={handleConfirmDeactivate}
        onCancel={() => setConfirmDeactivateItem(null)}
      />
    </div>
  );
}
